import React from 'react';
import { ArrowRight, ExternalLink, ChevronRight } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
export function OnFeedback() {
  const {
    language
  } = useLanguage();
  const isIS = language === 'is';
  const en = {
    back: 'Writing',
    badge: 'Five articles',
    title: 'On Feedback',
    intro: `There is a statement that reliably gets a room full of managers nodding: "We need feedback to learn, develop and grow." There is another statement that reliably gets those same managers shifting uncomfortably: "Can I give you some feedback?"

This is not a small irony. It is a fundamental contradiction sitting at the heart of how most organisations operate - and it has been sitting there, largely unexamined, for decades.

We have built entire industries around feedback. Performance review systems, 360-degree tools, continuous feedback platforms, manager training programmes. The assumption baked into all of it is straightforward: feedback is how people learn and improve, so more feedback, delivered better, will produce better outcomes.

It is a reasonable assumption. It is also, in important ways, wrong.

Not entirely wrong - which is part of what makes this complicated. But wrong enough to be worth examining carefully. The research on feedback is more ambiguous than the feedback industry acknowledges. What happens in the brain when someone offers to evaluate us is not what we would design if learning were the goal. And the idea that feedback is primarily about the recipient - a neutral transmission of useful information about their performance - turns out to be significantly less accurate than the alternative: that feedback reveals at least as much about the person giving it, and the system they operate within, as it does about the person receiving it.

This series starts from that contradiction and tries to work through it honestly. Not to argue that feedback is useless - it isn't - but to arrive at something more accurate, and therefore more useful, than what most organisations have settled for.`,
    articles: [{
      n: '01',
      title: 'The Feedback Paradox',
      desc: 'We know feedback is important. We also know most of it does not work. The first article names the paradox and begins to explain why.',
      href: 'https://helgig.substack.com/p/the-feedback-paradox',
      live: true
    }, {
      n: '02',
      title: "It's Not About You",
      desc: 'What feedback actually tells you - and why the insight that it reveals more about the giver than the receiver changes everything.',
      href: 'https://helgig.substack.com',
      live: false
    }, {
      n: '03',
      title: 'The Brightest Light Casts the Longest Shadow',
      desc: 'Why your strengths, taken too far, become your most reliable failure mode - and what that means for development.',
      href: 'https://helgig.substack.com',
      live: false
    }, {
      n: '04',
      title: 'Where You Are Changes What You Need',
      desc: 'The Dreyfus model applied to development: why what serves a novice actively harms an expert, and how to calibrate accordingly.',
      href: 'https://helgig.substack.com',
      live: false
    }, {
      n: '05',
      title: 'What To Actually Do With All Of This',
      desc: 'The practical conclusion: what a more honest approach to feedback and development actually looks like in practice.',
      href: 'https://helgig.substack.com',
      live: false
    }],
    substackCta: 'Follow on Substack to get each article as it publishes',
    relatedTitle: 'Related',
    related: [{
      label: 'Feedback Culture Workshop',
      href: '/services/workshops/feedback-culture'
    }, {
      label: 'Coaching Skills for Managers',
      href: '/services/workshops/coaching-skills'
    }]
  };
  const is = {
    back: 'Skrif',
    badge: 'Fimm greinar',
    title: 'Um endurgjöf',
    intro: `Endurgjöf: Þversögnin í hjarta stjórnunar

Það er til fullyrðing sem fær heilu fundarherbergin af stjórnendum til að kinka kolli í takt: „Við þurfum endurgjöf til að læra, þróast og vaxa." Svo er það hin fullyrðingin sem fær sömu stjórnendur til að iða óþægilega í sætunum: „Má ég gefa þér smá endurgjöf?"

Þetta er ekki lítilvæg kaldhæðni. Þetta er grundvallarþversögn í hjarta flestra skipulagsheilda – og hún hefur setið þar, að mestu órannsökuð, áratugum saman.

Við höfum byggt heilu iðnaðina í kringum endurgjöf. Frammistöðumat, 360 gráðu mælingar, hugbúnaðarlausnir fyrir stöðuga endurgjöf og þjálfunarkerfi fyrir stjórnendur. Undirliggjandi forsenda þessa alls er einföld: endurgjöf er leiðin til að þróa og bæta sig, þannig að meiri endurgjöf, veitt á betri hátt, muni skila betri árangri.

Þetta er rökrétt forsenda. En hún er líka, á mikilvægan hátt, röng. Ekki algjörlega röng – sem er hluti af því sem gerir þetta flókið. En nógu röng til þess að það sé þess virði að skoða hana vandlega.

Rannsóknir á endurgjöf eru mun tvíræðari en endurgjafariðnaðurinn vill vera láta. Það sem gerist í heilanum þegar einhver býðst til að leggja mat á okkur er ekki það ferli sem við myndum vilja setja af stað ef markmiðið væri lærdómur. Og sú hugmynd að endurgjöf snúist fyrst og fremst um þiggjandann – hlutlaus miðlun á gagnlegum upplýsingum um frammistöðu – reynist mun dekkri en valkosturinn: að endurgjöf segir að minnsta kosti jafn mikið um þann sem gefur hana, og kerfið sem hann starfar í, og hún segir um þann sem tekur á móti henni.

Þessi sería byrjar á þessari þversögn og reynir að vinna úr henni á heiðarlegan hátt. Ekki til að halda því fram að endurgjöf sé gagnslaus – það er hún ekki – heldur til að komast að niðurstöðu sem er nákvæmari, og þar af leiðandi gagnlegri, en það sem flest fyrirtæki láta sér nægja í dag.`,
    articles: [{
      n: '01',
      title: 'Endurgjöfarbreytan',
      desc: 'Við vitum að endurgjöf skiptir máli. Við vitum einnig að stærstur hluti hennar virkar ekki. Fyrsta greinin nefnir breytan og byrjar að skýra hvers vegna.',
      href: 'https://helgig.substack.com/p/the-feedback-paradox',
      live: true
    }, {
      n: '02',
      title: 'Þetta snertir þig ekki',
      desc: 'Hvað endurgjöf segir þér raunverulega - og hvers vegna innsæið um að hún opinberar meira um gefandann en móttakandann breytir öllu.',
      href: 'https://helgig.substack.com',
      live: false
    }, {
      n: '03',
      title: 'Bjartasti ljósið kastar lengsta skugga',
      desc: 'Hvers vegna styrkleikarnir þínir, teknir of langt, verða þæðar áreiðanlegasta bilunarhátturinn - og hvað það þýðir fyrir þróun.',
      href: 'https://helgig.substack.com',
      live: false
    }, {
      n: '04',
      title: 'Staðan þín breytir því sem þú þarft',
      desc: 'Dreyfus-líkanið beitt á þróun: hvers vegna það sem þjónar nýbyrja virkar gegn sérfræðingi, og hvernig á að aðlaga þess vegna.',
      href: 'https://helgig.substack.com',
      live: false
    }, {
      n: '05',
      title: 'Hvað á að gera með allt þetta',
      desc: 'Hagnýta niðurstaðan: hvernig heiðarlegri nálgun á endurgjöf og þróun lítur út í reynd.',
      href: 'https://helgig.substack.com',
      live: false
    }],
    substackCta: 'Fylgjast á Substack til að fá hverja grein þegar hún birtist',
    relatedTitle: 'Tengt',
    related: [{
      label: 'Endurgjöfarmót',
      href: '/services/workshops/feedback-culture'
    }, {
      label: 'Coaching Skills for Managers',
      href: '/services/workshops/coaching-skills'
    }]
  };
  const c = isIS ? is : en;
  return <PageTransition data-id="element-2453" data-mp="12wts1xagi16z">
      <div className="pt-12 pb-24" data-id="element-2454" data-mp="12xd97bsa8ljy">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2455" data-mp="17wzorrvl2dvx">
          <Animate data-id="element-2456" data-mp="17vepjoybq6rq">
            <Link to="/writing" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-teal mb-8 transition-colors" data-id="element-2457" data-mp="17umwolw42n55">
              <ChevronRight size={14} className="rotate-180" data-id="element-2458" data-mp="1goi2zx8yyjmd" />
              {c.back}
            </Link>
          </Animate>
          <Animate data-id="element-2459" data-mp="k1dgtzve7fzu">
            <span className="inline-block px-3 py-1 bg-teal/10 text-teal font-medium rounded-full text-xs mb-4" data-id="element-2460" data-mp="oxu12izh1a7n">
              {c.badge}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-charcoal mb-6" data-id="element-2461" data-mp="sye0xubfirxh">
              {c.title}
            </h1>
            <div className="prose prose-lg text-gray-500 leading-relaxed mb-12 space-y-4" data-id="element-2462" data-mp="xwfjme4y0p8f">
              {c.intro.split('\n\n').map((para, i) => <p key={i} data-id="element-2463" data-mp="29cxi0js3p1xp" data-mpi={i}>{para}</p>)}
            </div>
          </Animate>
          <div className="space-y-4 mb-12" data-id="element-2464" data-mp="2ji5ffk2ceg8s">
            {c.articles.map((a, i) => <Animate key={a.n} delay={i * 0.05} data-id="element-2465" data-mp="2eiapbiqe5uos" data-mpi={i}>
                {a.live ? <a href={a.href} target="_blank" rel="noopener noreferrer" className="block group" data-id="element-2466" data-mp="29s2n7m31p6bi">
                    <Card className="p-6 hover:shadow-md transition-all border border-gray-100 hover:border-teal/30" data-id="element-2467" data-mp="20r4i10gvesrc">
                      <div className="flex items-start gap-4" data-id="element-2468" data-mp="1vs1nwb2fi6y5">
                        <span className="text-2xl font-bold text-teal/30 font-mono flex-shrink-0 leading-none mt-1" data-id="element-2469" data-mp="1vunsq2xxq8co">
                          {a.n}
                        </span>
                        <div className="flex-1" data-id="element-2470" data-mp="1mtfvzpb2hfkp">
                          <div className="flex items-center gap-2 mb-1" data-id="element-2471" data-mp="3l7rakek7o7cu">
                            <h2 className="font-bold text-charcoal group-hover:text-teal transition-colors" data-id="element-2472" data-mp="3h5nxjlhw8upg">
                              {a.title}
                            </h2>
                            <span className="text-xs bg-teal/10 text-teal px-2 py-0.5 rounded-full font-medium" data-id="element-2473" data-mp="1qan4jnmbn9co">
                              Published
                            </span>
                          </div>
                          <p className="text-gray-500 text-sm leading-relaxed" data-id="element-2474" data-mp="1v7nbs1uwqi5d">
                            {a.desc}
                          </p>
                        </div>
                        <ExternalLink size={16} className="text-gray-300 group-hover:text-teal transition-colors flex-shrink-0 mt-1" data-id="element-2475" data-mp="17fztg8bpofep" />
                      </div>
                    </Card>
                  </a> : <Card className="p-6 border border-gray-100 opacity-60" data-id="element-2476" data-mp="1cfcecnj0vcen">
                    <div className="flex items-start gap-4" data-id="element-2477" data-mp="oq089wizrkgk">
                      <span className="text-2xl font-bold text-gray-200 font-mono flex-shrink-0 leading-none mt-1" data-id="element-2478" data-mp="jt9szbf0gsw7">
                        {a.n}
                      </span>
                      <div className="flex-1" data-id="element-2479" data-mp="306jxwgeiiw33">
                        <div className="flex items-center gap-2 mb-1" data-id="element-2480" data-mp="307bspp0acpsb">
                          <h2 className="font-bold text-charcoal" data-id="element-2481" data-mp="2mfotpheyl35u">{a.title}</h2>
                          <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full font-medium" data-id="element-2482" data-mp="2qgqonm1uet20">
                            Coming soon
                          </span>
                        </div>
                        <p className="text-gray-400 text-sm leading-relaxed" data-id="element-2483" data-mp="3j5xsn0k2gaun">
                          {a.desc}
                        </p>
                      </div>
                    </div>
                  </Card>}
              </Animate>)}
          </div>
          <Animate data-id="element-2484" data-mp="1tqrclhmhhceo">
            <Card className="p-6 bg-teal/5 border-teal/20 border text-center mb-12" data-id="element-2485" data-mp="1tq856uugm6ew">
              <p className="text-gray-600 mb-4" data-id="element-2486" data-mp="1yq41s90tuvkf">{c.substackCta}</p>
              <a href="https://helgig.substack.com" target="_blank" rel="noopener noreferrer" data-id="element-2487" data-mp="1yn82ohd8u0ue">
                <Button variant="outline" rightIcon={<ExternalLink size={16} data-id="element-2489" data-mp="3qof6tk2w0dty" />} data-id="element-2488" data-mp="3vgymqnp1rl1x">
                  Substack
                </Button>
              </a>
            </Card>
          </Animate>
          <Animate data-id="element-2490" data-mp="3mlivmefofk01">
            <div data-id="element-2491" data-mp="3hnz9ytgng4y2">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3" data-id="element-2492" data-mp="3hoioe073cb7c">
                {c.relatedTitle}
              </h3>
              <div className="flex flex-wrap gap-3" data-id="element-2493" data-mp="3cr8ti50xn7c4">
                {c.related.map(r => <Link key={r.href} to={r.href} data-id="element-2494" data-mp="37s5mif1e6r1i">
                    <Button variant="outline" size="sm" rightIcon={<ArrowRight size={14} data-id="element-2496" data-mp="15xog50dcg1ih" />} data-id="element-2495" data-mp="33p9bpai3fz84">
                      {r.label}
                    </Button>
                  </Link>)}
              </div>
            </div>
          </Animate>
        </div>
      </div>
    </PageTransition>;
}